import { useState } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import type { CreatorPoolItem, OutreachPreview, PoolSource } from '@/api/client'
import { SOURCE_LABEL, fmtCount, fmtRate, previewSentence, usePoolMembers } from '@/lib/creators'
import { Button, Field, FieldGroup, Input, PlatformMark, SearchInput, Select, Skeleton, errorText } from '@/ui'
import { criteriaSummary } from '@/lib/creators'
import type { WizardState } from './wizard'

export interface StepProps {
  s: WizardState
  set: (patch: Partial<WizardState>) => void
}

/** The sentence under the filters: how many can be contacted and who was left out. */
export function PreviewLine({ q, problem }: { q: UseQueryResult<OutreachPreview>; problem?: string | null }) {
  if (problem) return <p role="alert" className="text-table text-status-failed">{problem}</p>
  if (q.error && !q.data) return <p role="alert" className="text-table text-status-failed">{errorText(q.error)}</p>
  if (!q.data) return <Skeleton className="h-6 w-2/3" />
  return (
    <p role="status" aria-label="미리 보기 결과" className="text-body">
      <b className="tabular-nums">{previewSentence(q.data)}</b>
      {q.isFetching && <span className="ml-2 text-meta text-muted-foreground">다시 세는 중</span>}
    </p>
  )
}

/** Optional: choose people from the pool by hand. When anyone is chosen only they are taken. */
function Picker({ s, set }: StepProps) {
  const [q, setQ] = useState('')
  const res = usePoolMembers(s.poolId, { status: 'in', sort: 'score', limit: 200, q: q.trim() || undefined })
  const items = res.data?.items ?? []
  const toggle = (id: number) => set({ picked: s.picked.includes(id) ? s.picked.filter((x) => x !== id) : [...s.picked, id] })
  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput value={q} onChange={setQ} placeholder="아이디·이름 찾기" />
        <span className="text-meta text-muted-foreground tabular-nums">{s.picked.length}명 골랐다</span>
        {s.picked.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => set({ picked: [] })}>
            비우기
          </Button>
        )}
      </div>
      {res.isPending ? (
        <Skeleton className="h-24" />
      ) : items.length === 0 ? (
        <p className="text-table text-muted-foreground">보여 줄 사람이 없다.</p>
      ) : (
        <ul className="max-h-72 divide-y divide-border overflow-y-auto" aria-label="풀의 사람">
          {items.map((m) => {
            const c = m.creator
            const blocked = m.suppressed
            return (
              <li key={c.id}>
                <label className="flex min-w-0 cursor-pointer items-center gap-3 py-2 text-table">
                  <input
                    type="checkbox"
                    className="h-4 w-4 shrink-0"
                    checked={s.picked.includes(c.id)}
                    onChange={() => toggle(c.id)}
                    aria-label={`@${c.handle} 고르기`}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">@{c.handle}</span>
                    <span className="block truncate text-meta text-muted-foreground">
                      {c.display_name ? `${c.display_name} · ` : ''}팔로워 {fmtCount(c.followers)}
                      {c.engagement_rate > 0 ? ` · 참여율 ${fmtRate(c.engagement_rate)}` : ''} · 점수 {Math.round(m.score)}
                    </span>
                  </span>
                  {blocked && <span className="shrink-0 text-meta text-status-failed">연락 금지</span>}
                  {!blocked && m.last_contacted_at && <span className="shrink-0 text-meta text-muted-foreground">연락한 적 있음</span>}
                </label>
              </li>
            )
          })}
        </ul>
      )}
      {res.data && res.data.total > items.length && (
        <p className="text-meta text-muted-foreground">앞의 {items.length}명만 보인다. 이름으로 찾아 고른다.</p>
      )}
    </div>
  )
}

/** Step 1 — 대상: the pool, the filters, and how many can be reached. */
export function StepTarget({
  s,
  set,
  pools,
  preview,
  problem,
}: StepProps & { pools: CreatorPoolItem[]; preview: UseQueryResult<OutreachPreview>; problem: string | null }) {
  const [picking, setPicking] = useState(s.picked.length > 0)
  const pool = pools.find((p) => p.id === s.poolId)
  const toggleSource = (src: PoolSource) =>
    set({ sources: s.sources.includes(src) ? s.sources.filter((x) => x !== src) : [...s.sources, src] })
  return (
    <div className="space-y-5">
      <Field label="풀" required hint={pool ? criteriaSummary(pool) : '연락할 사람이 들어 있는 풀을 고른다.'}>
        <Select
          value={s.poolId || ''}
          onChange={(e) => {
            const p = pools.find((x) => x.id === Number(e.target.value))
            set({ poolId: Number(e.target.value), picked: [], ...(p ? { language: p.language } : {}) })
          }}
        >
          <option value="">풀을 고른다</option>
          {pools.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} · {p.platform === 'instagram' ? 'Instagram' : 'Bluesky'} · {fmtCount(p.members_in)}명
            </option>
          ))}
        </Select>
      </Field>
      {pool && (
        <>
          <FieldGroup title="조건" description="비워 두면 그 조건은 보지 않는다." columns={2}>
            <Field label="팔로워 최소">
              <Input inputMode="numeric" value={s.fMin} onChange={(e) => set({ fMin: e.target.value })} placeholder="예: 1000" />
            </Field>
            <Field label="팔로워 최대">
              <Input inputMode="numeric" value={s.fMax} onChange={(e) => set({ fMax: e.target.value })} placeholder="예: 100000" />
            </Field>
            <Field label="최소 점수" hint="0~100">
              <Input inputMode="numeric" value={s.score} onChange={(e) => set({ score: e.target.value })} placeholder="예: 60" />
            </Field>
            <Field label="최소 참여율 (%)" hint="좋아요와 댓글이 팔로워의 몇 %인지">
              <Input inputMode="decimal" value={s.engagement} onChange={(e) => set({ engagement: e.target.value })} placeholder="예: 3" />
            </Field>
          </FieldGroup>
          <fieldset className="min-w-0 space-y-2">
            <legend className="text-meta font-medium">찾은 경로</legend>
            <div className="flex flex-wrap gap-2">
              {(Object.entries(SOURCE_LABEL) as [PoolSource, string][]).map(([k, label]) => (
                <label key={k} className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-table">
                  <input type="checkbox" checked={s.sources.includes(k)} onChange={() => toggleSource(k)} />
                  {label}
                </label>
              ))}
            </div>
            <p className="text-meta text-muted-foreground">고르지 않으면 모든 경로를 본다.</p>
          </fieldset>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-meta font-medium">
                <PlatformMark platform={pool.platform} /> 직접 고르기
              </span>
              <Button size="sm" onClick={() => setPicking((v) => !v)}>
                {picking ? '접기' : '사람 직접 고르기'}
              </Button>
            </div>
            {picking && <Picker s={s} set={set} />}
            {!picking && s.picked.length > 0 && <p className="text-meta text-muted-foreground">직접 고른 {s.picked.length}명만 대상이다.</p>}
          </div>
          <div className="rounded-md bg-muted/60 p-3">
            <PreviewLine q={preview} problem={problem} />
          </div>
        </>
      )}
    </div>
  )
}
